import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import {
  Box, IconButton, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import { CardWrapper, SectionTitle } from '../UI';
import * as C from '../../theme/colors.constants';
import { DashboardController } from '../../../controller/dashboardController';
import { DateUtils } from '../../../helpers/utils/date.utils';
import usePodeGerenciarDashboard from '../../hooks/usePodeGerenciarDashboard';
import CalendarioResizeHandle from './CalendarioResizeHandle';
import { calcularLimitesRatio } from './calcularLimitesRatio';
import EditarEventosCalendarioDialog from './EditarEventosCalendarioDialog';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const MIN_ESQUERDA_PX = 150;
const MIN_DIREITA_PX = 110;
const RATIO_PADRAO = 0.6;

/**
 * Calendário da home.
 *
 * Substitui a dependência `react-calendar@2` (que não roda em React 18) por uma
 * grade própria de 6x7 células — é pouco código e evita travar o protótipo numa
 * lib legada. Visual mantido igual ao print: dias de outros meses em cinza
 * claro, dia selecionado num círculo `#3d3a6b`, e o painel do dia à direita.
 */
export default function CalendarioCard() {
  const hoje = DateUtils.getDateWithoutTime();
  const [mesRef, setMesRef] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const [selecionado, setSelecionado] = useState(hoje);
  const [eventosDoDia, setEventosDoDia] = useState([]);
  const [dialogAberto, setDialogAberto] = useState(false);
  const podeGerenciar = usePodeGerenciarDashboard();

  const containerRef = useRef(null);
  const [ratio, setRatio] = useState(RATIO_PADRAO);

  const clamparRatio = useCallback((valor) => {
    const largura = containerRef.current ? containerRef.current.getBoundingClientRect().width : 0;
    const { min, max } = calcularLimitesRatio(largura, MIN_ESQUERDA_PX, MIN_DIREITA_PX);
    return Math.min(Math.max(valor, min), max);
  }, []);

  useEffect(() => {
    const reclampar = () => setRatio(atual => clamparRatio(atual));
    reclampar();
    window.addEventListener('resize', reclampar);
    return () => window.removeEventListener('resize', reclampar);
  }, [clamparRatio]);

  const buscarEventosDoDia = useCallback(async () => {
    const eventos = await DashboardController.findAllDashboardCalendarEventByDate(selecionado);
    setEventosDoDia(eventos);
  }, [selecionado]);

  useEffect(() => {
    buscarEventosDoDia().then();
  }, [buscarEventosDoDia]);

  const celulas = useMemo(() => {
    const primeiro = new Date(mesRef.getFullYear(), mesRef.getMonth(), 1);
    const inicio = new Date(primeiro);
    inicio.setDate(1 - primeiro.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(inicio);
      d.setDate(inicio.getDate() + i);
      return d;
    });
  }, [mesRef]);

  const mesmoDia = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

  const mudarMes = delta => setMesRef(new Date(mesRef.getFullYear(), mesRef.getMonth() + delta, 1));

  return (
    <CardWrapper>
      <SectionTitle
        right={(
          <Tooltip title={podeGerenciar ? 'Editar' : 'Você não tem permissão para editar'}>
            <span>
              <IconButton
                size="small"
                aria-label="Editar Eventos do Calendário"
                disabled={!podeGerenciar}
                onClick={() => setDialogAberto(true)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      >
        Calendário
      </SectionTitle>

      <Box
        ref={containerRef}
        sx={{
          display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        <Box sx={{ flex: { xs: '1 1 100%', md: `1 1 ${ratio * 100}%` }, minWidth: MIN_ESQUERDA_PX }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1,
          }}
          >
            <IconButton size="small" aria-label="Mês anterior" onClick={() => mudarMes(-1)} sx={{ color: C.navy }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{
              fontWeight: 700, color: C.navy, minWidth: 130, textAlign: 'center',
            }}
            >
              {MESES[mesRef.getMonth()]}
              {' '}
              {mesRef.getFullYear()}
            </Typography>
            <IconButton size="small" aria-label="Próximo mês" onClick={() => mudarMes(1)} sx={{ color: C.navy }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', rowGap: 0.5 }}>
            {DIAS.map(d => (
              <Typography
                key={d}
                variant="caption"
                sx={{
                  textAlign: 'center', fontWeight: 700, color: C.navy, pb: 0.5,
                }}
              >
                {d}
              </Typography>
            ))}

            {celulas.map((d) => {
              const foraDoMes = d.getMonth() !== mesRef.getMonth();
              const ativo = mesmoDia(d, selecionado);
              return (
                <Box
                  key={d.toISOString()}
                  onClick={() => setSelecionado(d)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'clamp(26px, 1.7vw, 38px)',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: 'clamp(24px, 1.55vw, 34px)',
                      height: 'clamp(24px, 1.55vw, 34px)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: ativo ? '#3d3a6b' : 'transparent',
                      // eslint-disable-next-line no-nested-ternary
                      color: ativo ? C.white : foraDoMes ? '#c3cbd6' : C.black,
                      fontWeight: ativo ? 700 : 400,
                      fontSize: 'clamp(11px, 0.66vw, 15px)',
                      '&:hover': { backgroundColor: ativo ? '#3d3a6b' : C.paleGrey },
                    }}
                  >
                    {d.getDate()}
                  </Box>
                  {/* Indicador de "dia com evento" removido nesta etapa — a API real
                      busca eventos por dia, não por mês, então precisaria de N chamadas
                      por mês visível para recalcular isso. Débito técnico documentado
                      em docs/plano-remocao-mocks-home-etapa5.md. */}
                </Box>
              );
            })}
          </Box>
        </Box>

        <CalendarioResizeHandle
          containerRef={containerRef}
          ratio={ratio}
          onChange={setRatio}
          minLeftPx={MIN_ESQUERDA_PX}
          minRightPx={MIN_DIREITA_PX}
          defaultRatio={RATIO_PADRAO}
        />

        <Box sx={{ flex: { xs: '1 1 100%', md: `1 1 ${(1 - ratio) * 100}%` }, minWidth: MIN_DIREITA_PX }}>
          <Typography sx={{ fontWeight: 700, color: C.navy, mb: 1 }}>
            {selecionado.getDate()}
            {' '}
            de
            {' '}
            {MESES[selecionado.getMonth()].toLowerCase()}
          </Typography>
          {eventosDoDia.length === 0 ? (
            <Typography variant="body2" sx={{ color: C.greyish }}>
              Nenhum evento neste dia.
            </Typography>
          ) : (
            eventosDoDia.map(evento => (
              <Box
                key={evento.key}
                sx={{
                  display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.25,
                }}
              >
                <Box
                  component="i"
                  className={evento.icone}
                  sx={{
                    fontSize: 16, color: C.teal, mt: 0.25, flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600, flex: 1, minWidth: 0, wordBreak: 'break-word',
                  }}
                >
                  {evento.descricao}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>

      <EditarEventosCalendarioDialog
        open={dialogAberto}
        selecionado={selecionado}
        onClose={() => setDialogAberto(false)}
        onPublicado={buscarEventosDoDia}
      />
    </CardWrapper>
  );
}
